import { useEffect, useState } from "react";
import api, { errMsg } from "../api";
import { fmtCHF, UNITS, MODULES } from "../utils";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

const EMPTY = { name: "", price: "", unit: "piece", module: "laundry" };

export default function PriceList() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("laundry");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/price-list").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, price: parseFloat(form.price) || 0 };
      if (editing) await api.put(`/price-list/${editing.id}`, payload);
      else await api.post("/price-list", payload);
      toast.success(editing ? "Article modifié" : "Article ajouté");
      setOpen(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Retirer « ${p.name} » de la liste des prix ?`)) return;
    try { await api.delete(`/price-list/${p.id}`); toast.success("Article retiré"); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
  const visible = items.filter((p) => p.module === tab && norm(p.name).includes(norm(search)));

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-5" data-testid="price-list-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Listes des prix</h1>
          <p className="text-sm text-slate-500 mt-1">Une liste par module. Prix verrouillés pour les employés : ils saisissent uniquement les quantités. Les rabais se font par article ou par facture.</p>
        </div>
        <button data-testid="create-price-button" onClick={() => { setEditing(null); setForm({ ...EMPTY, module: tab }); setOpen(true); }}
          className="flex items-center gap-2 bg-sky-600 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-sky-700 transition-colors">
          <Plus size={16} /> Nouvel article
        </button>
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="price-module-tabs">
        {Object.entries(MODULES).map(([k, M]) => (
          <button key={k} data-testid={`price-tab-${k}`} onClick={() => setTab(k)}
            className={`flex items-center gap-2 px-3.5 py-1.5 rounded-md text-sm border transition-colors ${tab === k ? "border-transparent text-white font-medium" : "bg-white text-slate-500 border-slate-200 hover:text-slate-900"}`}
            style={tab === k ? { backgroundColor: M.color } : {}}>
            <M.icon size={14} /> {M.label}
          </button>
        ))}
      </div>

      <input data-testid="price-search" type="search" placeholder="Rechercher un article…" value={search} onChange={(e) => setSearch(e.target.value)}
        className="w-full max-w-sm rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 focus:outline-none" />

      <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-100">
              <th className="px-5 py-3">Article</th><th className="px-5 py-3">Prix</th><th className="px-5 py-3">Unité</th><th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-testid="price-list-table">
            {visible.map((p) => (
              <tr key={p.id} data-testid={`price-row-${p.id}`} className="hover:bg-slate-50">
                <td className="px-5 py-3 font-medium text-slate-900">{p.name}</td>
                <td className="px-5 py-3 font-semibold text-sky-700">{fmtCHF(p.price)}</td>
                <td className="px-5 py-3 text-slate-600">{UNITS[p.unit] || p.unit}</td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  <button data-testid={`edit-price-${p.id}`} onClick={() => { setEditing(p); setForm({ name: p.name, price: p.price, unit: p.unit, module: p.module || "laundry" }); setOpen(true); }}
                    className="text-slate-400 hover:text-slate-900 p-1"><Pencil size={15} /></button>
                  <button data-testid={`delete-price-${p.id}`} onClick={() => remove(p)} className="text-slate-400 hover:text-red-600 p-1"><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
            {visible.length === 0 && <tr><td colSpan={4} className="px-5 py-8 text-center text-slate-400">{search ? "Aucun article ne correspond à la recherche." : "Aucun article. Créez votre liste des prix."}</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" data-testid="price-form-modal">
          <form onSubmit={save} className="bg-white rounded-lg border border-slate-200 shadow-lg w-full max-w-sm p-6 space-y-4">
            <h2 className="text-base font-semibold text-slate-900">{editing ? "Modifier l'article" : "Nouvel article"}</h2>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Nom *</label>
              <input data-testid="price-form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ex : Chemise, Housse de couette, Linge au kg…"
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-sky-600 focus:outline-none" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Prix (CHF) *</label>
                <input data-testid="price-form-price" type="number" step="0.05" min="0" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-sky-600 focus:outline-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Unité</label>
                <select data-testid="price-form-unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white">
                  {Object.entries(UNITS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Module</label>
              <select data-testid="price-form-module" value={form.module} onChange={(e) => setForm({ ...form, module: e.target.value })}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white">
                {Object.entries(MODULES).map(([k, M]) => <option key={k} value={k}>{M.label}</option>)}
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" data-testid="price-form-cancel" onClick={() => setOpen(false)} className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100">Annuler</button>
              <button type="submit" data-testid="price-form-submit" className="px-4 py-2 rounded-lg text-sm font-semibold bg-sky-600 text-white hover:bg-sky-700">Enregistrer</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
